import { Form } from '@form/Form';
import { FormDivider } from '@form/FormDivider';
import { FormMapping } from '@form/FormMapping';
import { Blockquote, Button } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { FilterState } from 'components/DataTable/DataTable';
import { FormInput } from 'components/Form/next';
import { canonicalizeMapping, MappingValue } from '@form/formMappingValue';
import Loader from 'components/Loader';
import { useApi } from 'hooks/useApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import MatchPreviewTable, { computeCollisions } from './MatchPreviewTable';
import ResultSummary from './ResultSummary';
import { CommitResult, PreviewResponse, RowDecision } from './types';

interface Props {
    eventId: number;
    formId?: number;
    /** null means "every booking of the event" (table-wide selection). */
    bookingIds: number[] | null;
    /** Table filter in effect when bookingIds is null; forwarded so "select all" respects it server-side. */
    filter?: FilterState;
    callback?: () => void;
}

interface FormValueBridgeProps {
    onMappingChange: (value: Record<string, MappingValue>) => void;
}

// `Form`'s onChange is the plain DOM handler and cannot read react-hook-form values, so this child (rendered inside the Form/FormProvider) watches `form_mapping` and pushes changes up to the assistant.
const FormValueBridge = ({ onMappingChange }: FormValueBridgeProps) => {
    // useWatch, not watch(): under the shared Form a watch() child never re-renders, so edits never arrived.
    const formMapping = useWatch({ name: 'form_mapping' });

    // Forwards every content change of the mapping to the parent, debounced there.
    useEffect(() => {
        if (formMapping) onMappingChange(formMapping);
    }, [JSON.stringify(formMapping)]);

    return null;
};

/** Assistant that turns one or many event bookings into club members. */
const BookingToClubMemberAssistant = ({ eventId, formId, bookingIds, filter, callback }: Props) => {
    // Translated strings live under the "bookings" namespace.
    const { t } = useTranslation('bookings');
    const api = useApi();
    const clubSlug = useClubSlug();

    const [preview, setPreview] = useState<PreviewResponse | null>(null);
    const [decisions, setDecisions] = useState<Record<number, RowDecision>>({});
    const [mapping, setMapping] = useState<Record<string, MappingValue> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<CommitResult | null>(null);
    const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Booking ids whose decision the user explicitly set (via handleDecisionChange); anything else is
    // auto-seeded fresh from the latest suggestion on every preview response.
    const touchedRows = useRef<Set<number>>(new Set());
    // Canonicalized mapping this component last sent to or received from the backend, used to tell a real
    // user edit apart from the FormMapping prefill echoing the mapping we already have in effect.
    const effectiveMapping = useRef<string>('');
    // Incremented on every preview request; lets a success handler detect it was superseded by a later request.
    const requestSeq = useRef(0);

    // Club properties, used as extra selectable mapping targets alongside the fixed member fields.
    const { data: { data: properties } = {} } = useSWR<{ data: Array<{ key: string; title: string }> }>(
        `club-member/${clubSlug}/property`
    );
    // Club member categories, offered in the "assign to categories" select.
    const { data: { data: categories } = {} } = useSWR<{ data: Array<{ id: number; title: string }> }>(
        `club-member/${clubSlug}/category`
    );

    /** Labels for the mapping selects: the fixed member fields plus club properties. */
    const mappedFields = useMemo(
        () => ({
            firstname: t('booking.createMember.fields.firstname'),
            lastname: t('booking.createMember.fields.lastname'),
            name: t('booking.createMember.fields.name'),
            gender: t('booking.createMember.fields.gender'),
            birthday: t('booking.createMember.fields.birthday'),
            phone: t('booking.createMember.fields.phone'),
            email: t('booking.createMember.fields.email'),
            street: t('booking.createMember.fields.street'),
            streetnumber: t('booking.createMember.fields.streetnumber'),
            postalcode: t('booking.createMember.fields.postalcode'),
            city: t('booking.createMember.fields.city'),
            entry_date: t('booking.createMember.fields.entry_date'),
            ...(properties ?? []).reduce((carry, property) => ({ ...carry, [property.key]: property.title }), {})
        }),
        [properties, t]
    );

    /** Loads the preview for the given mapping and (re-)seeds the per-row decisions, keeping only user-touched ones. */
    const loadPreview = useCallback(
        (currentMapping: Record<string, MappingValue> | null) => {
            // Marks this call as the latest in-flight request; used below to drop superseded responses.
            const requestId = ++requestSeq.current;

            return api({
                url: `club-member/${clubSlug}/bookings/preview`,
                method: 'POST',
                muted: true,
                data: { event_id: eventId, booking_ids: bookingIds, filter, form_mapping: currentMapping },
                onSuccess: (data: PreviewResponse) => {
                    // A newer request has since been issued (e.g. another rapid mapping edit); this response is stale.
                    if (requestId !== requestSeq.current) return;

                    setPreview(data);
                    // Only adopt the backend's auto-detected mapping once (the initial load); later reloads
                    // are always caused by the user editing the mapping, whose value must be kept as-is.
                    setMapping((current) => current ?? data.form_mapping);
                    // The mapping now in effect is whatever the backend just processed.
                    effectiveMapping.current = canonicalizeMapping(data.form_mapping);

                    // Booking ids that dropped out of this preview can't stay "touched" forever.
                    const rowIds = new Set(data.rows.map((row) => row.booking_id));
                    touchedRows.current.forEach((bookingId) => {
                        if (!rowIds.has(bookingId)) touchedRows.current.delete(bookingId);
                    });

                    setDecisions((current) => {
                        const seeded: Record<number, RowDecision> = {};
                        const used: number[] = [];

                        data.rows.forEach((row) => {
                            const existing = current[row.booking_id];
                            // A decision the user explicitly made for this row survives the reload untouched,
                            // and keeps occupying its merge target so a later row can't be auto-seeded onto it.
                            if (touchedRows.current.has(row.booking_id) && existing) {
                                seeded[row.booking_id] = existing;
                                if (existing.action === 'merge' && existing.member_id) used.push(existing.member_id);
                                return;
                            }

                            const isCollision =
                                row.suggested_action === 'merge' &&
                                row.suggested_member_id !== null &&
                                used.includes(row.suggested_member_id);

                            if (row.suggested_action === 'merge' && row.suggested_member_id && !isCollision) {
                                used.push(row.suggested_member_id);
                                seeded[row.booking_id] = { action: 'merge', member_id: row.suggested_member_id };
                                return;
                            }

                            // Rows suggested to create, or merge-suggestions whose target is already taken,
                            // start as create/skip respectively.
                            seeded[row.booking_id] = {
                                action: isCollision ? 'skip' : 'create',
                                member_id: null
                            };
                        });

                        return seeded;
                    });
                }
            });
        },
        [api, clubSlug, eventId, bookingIds, filter]
    );

    // Loads the preview once on mount with no mapping, so the backend returns its auto-detected mapping;
    // later reloads are triggered explicitly by handleMappingChange instead of this effect's deps.
    useEffect(() => {
        loadPreview(null);
    }, []);

    // Clears any pending debounced reload when the assistant unmounts.
    useEffect(
        () => () => {
            if (debounce.current) clearTimeout(debounce.current);
        },
        []
    );

    /** Re-runs the preview 400 ms after the last mapping change, ignoring the FormMapping prefill echo. */
    const handleMappingChange = useCallback(
        (next: Record<string, MappingValue>) => {
            // Always keep the latest edit in state, even if no reload ends up being scheduled for it.
            setMapping(next);

            // Cancel any pending reload; whether a new one is needed is decided fresh below.
            if (debounce.current) clearTimeout(debounce.current);

            // Not a real edit: FormMapping just prefilled the mapping we already have in effect. The timer
            // is already cleared above, so a reverted edit can't trigger an outdated reload.
            if (canonicalizeMapping(next) === effectiveMapping.current) return;

            debounce.current = setTimeout(() => {
                // This debounced reload is what puts `next` into effect.
                effectiveMapping.current = canonicalizeMapping(next);
                loadPreview(next);
            }, 400);
        },
        [loadPreview]
    );

    /** Stores the decision the user made for a single row and marks it as user-owned so reloads keep it. */
    const handleDecisionChange = useCallback((bookingId: number, decision: RowDecision) => {
        touchedRows.current.add(bookingId);
        setDecisions((current) => ({ ...current, [bookingId]: decision }));
    }, []);

    /** Sends the final decisions to the backend and shows the summary; category_ids comes straight from the submit data. */
    const handleSubmit = useCallback(
        (formData: FieldValues) => {
            setIsLoading(true);

            return api({
                url: `club-member/${clubSlug}/bookings/members`,
                method: 'POST',
                data: {
                    event_id: eventId,
                    filter,
                    form_mapping: formData.form_mapping ?? mapping,
                    category_ids: (formData.category_ids ?? []).map(Number),
                    rows: Object.entries(decisions).map(([bookingId, decision]) => ({
                        booking_id: Number(bookingId),
                        action: decision.action,
                        member_id: decision.action === 'merge' ? decision.member_id : null
                    }))
                },
                onBefore: () => setIsLoading(false),
                onSuccess: (commitResult: CommitResult) => setResult(commitResult)
            });
        },
        [api, clubSlug, eventId, mapping, decisions, filter]
    );

    /** Counts of each action, shown on the submit button. */
    const balance = useMemo(() => {
        const values = Object.values(decisions);

        return {
            create: values.filter((decision) => decision.action === 'create').length,
            merge: values.filter((decision) => decision.action === 'merge').length,
            skip: values.filter((decision) => decision.action === 'skip').length
        };
    }, [decisions]);

    // Rows that currently target the same member as an earlier row; the backend rejects the whole batch
    // if any survive to submission, so they must block the submit button rather than just being highlighted.
    const hasCollision = useMemo(
        () => (preview ? Object.keys(computeCollisions(preview.rows, decisions)).length > 0 : false),
        [preview, decisions]
    );

    // Once the commit succeeded, the summary replaces the form; only its close button ends the dialog.
    if (result) {
        return <ResultSummary result={result} onClose={() => callback?.()} />;
    }

    return (
        <Loader isLoading={!preview}>
            <Blockquote>{t('booking.createMember.intro')}</Blockquote>

            <Form onSubmit={handleSubmit}>
                <FormValueBridge onMappingChange={handleMappingChange} />

                <FormInput.SelectMultiple
                    name="category_ids"
                    label={t('booking.createMember.categoriesLabel')}
                    options={(categories ?? []).map((category) => ({ key: category.id, label: category.title }))}
                    buttonProps={{ block: true }}
                />

                {formId && (
                    <FormDivider
                        title={t('booking.createMember.mappingSection')}
                        collapsible
                        defaultExpanded
                        fieldKeys={['form_mapping']}
                    >
                        <FormMapping
                            hideLabel
                            multiple
                            disableGuessing
                            formId={formId}
                            mappedFields={mappedFields}
                            defaultMapping={preview?.form_mapping}
                            description={t('booking.createMember.mappingDescription')}
                        />
                    </FormDivider>
                )}

                {preview && (
                    <MatchPreviewTable rows={preview.rows} decisions={decisions} onChange={handleDecisionChange} />
                )}

                <Button
                    type="submit"
                    disabled={isLoading || hasCollision || preview?.rows.length === 0}
                    className="tw-mt-4 tw-w-full"
                >
                    {t('booking.createMember.submit', balance)}
                </Button>
                {hasCollision && (
                    <p className="tw-m-0 tw-mt-1 tw-text-center tw-text-sm tw-text-error">
                        {t('booking.createMember.collisionBlocked')}
                    </p>
                )}
            </Form>
        </Loader>
    );
};

export default BookingToClubMemberAssistant;
